import { useState } from 'react'
import { installHelp, inviteText, type Step, type StepId } from '../../lib/getStarted'
import { CheckIcon } from '../icons'

// Get started (lib/getStarted.ts decides what is done), opened from its button
// beside Stats (components/shell/AppShell) into a sheet. Each step says what to do and, where the app can do it, offers the
// button; nothing here is ticked by a tap, only by the thing having happened.
// Hide puts it away for good on this device (Settings brings it back); once
// every step is done, the button goes by itself.

export interface GetStartedProps {
  steps: Step[]
  householdName: string
  inviteCode: string
  /** The phone layout: quick add sits at the bottom there, at the top on desktop. */
  phone: boolean
  /** The browser's install prompt (Android), when it offers one. */
  installPrompt: (() => Promise<void>) | null
  userAgent?: string
  onHide: () => void
  onNoWatch: () => void
  onPairWatch: () => void
  onRecipes: () => void
  onOpenSettings: () => void
  /** In the sheet: the Done button that closes it. */
  onClose?: () => void
}

const TITLES: Record<StepId, string> = {
  invite: 'Invite your household',
  install: 'Add Matlist to your home screen',
  items: 'Put something on the list',
  recipe: 'Save a recipe',
  watch: 'Pair your Garmin',
}

export function GetStarted(p: GetStartedProps) {
  const [copied, setCopied] = useState(false)
  // One step open at a time, so the card stays short above the list: the next
  // one to do, or whichever the user tapped.
  const [picked, setPicked] = useState<StepId | null>(null)
  const done = p.steps.filter(s => s.done).length
  const open = p.steps.find(s => s.id === picked && !s.done)?.id ?? p.steps.find(s => !s.done)?.id

  async function invite() {
    const text = inviteText(p.householdName, p.inviteCode, `${window.location.origin}/`)
    if (navigator.share) {
      try { await navigator.share({ text }); return } catch (e) {
        if ((e as Error)?.name === 'AbortError') return
      }
    }
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      p.onOpenSettings()
    }
  }

  function help(id: StepId): string {
    switch (id) {
      case 'invite': return `A shared list works once they’re on it. Their code is ${p.inviteCode}.`
      case 'install': {
        const how = installHelp(p.userAgent ?? navigator.userAgent, !!p.installPrompt)
        if (how === 'ios') return 'In Safari, tap Share, then Add to Home Screen. Matlist then opens like any app.'
        if (how === 'android-prompt') return 'Then Matlist opens like any app, full screen.'
        if (how === 'android') return 'Open the browser menu and tap Install app or Add to Home screen.'
        return 'Open your browser’s menu and add Matlist to the home screen.'
      }
      case 'items': return p.phone ? 'Type it in the box at the bottom. Everyone in the household sees it at once.' : 'Type it in the box above. Everyone in the household sees it at once.'
      case 'recipe': return 'Paste a link from a recipe site, TikTok, Instagram or YouTube, and add its ingredients in one go.'
      case 'watch': return 'Tick things off on your wrist while you shop.'
    }
  }

  function actions(id: StepId) {
    switch (id) {
      case 'invite': return <button type="button" className="ml-btn ml-btn--primary ml-start__btn" onClick={invite}>{copied ? 'Copied' : 'Invite'}</button>
      case 'install': return p.installPrompt
        ? <button type="button" className="ml-btn ml-btn--primary ml-start__btn" onClick={() => { void p.installPrompt?.() }}>Install</button>
        : null
      case 'recipe': return <button type="button" className="ml-btn ml-btn--secondary ml-start__btn" onClick={p.onRecipes}>Recipes</button>
      case 'watch': return (
        <>
          <button type="button" className="ml-btn ml-btn--secondary ml-start__btn" onClick={p.onPairWatch}>Pair</button>
          <button type="button" className="ml-text-btn ml-start__skip" onClick={p.onNoWatch}>No Garmin</button>
        </>
      )
      default: return null
    }
  }

  return (
    <section className={`ml-start${p.onClose ? ' ml-start--sheet' : ''}`} aria-labelledby="ml-start-title">
      {p.onClose ? (
        <header className="ml-start__head ml-start__head--sheet">
          <div className="ml-start__heading">
            <h2 id="ml-start-title" className="ml-display ml-sheet-title ml-start__title">Get started</h2>
            <span className="ml-label ml-start__count ml-num">{done} of {p.steps.length} done</span>
          </div>
          <button type="button" className="ml-btn ml-btn--secondary ml-start__done" onClick={p.onClose}>Done</button>
        </header>
      ) : (
        <header className="ml-start__head">
          <h2 id="ml-start-title" className="ml-start__title">Get started</h2>
          <span className="ml-label ml-start__count ml-num">{done} of {p.steps.length} done</span>
          <button type="button" className="ml-text-btn ml-start__hide" onClick={p.onHide}>Hide</button>
        </header>
      )}
      <ol className="ml-start__steps">
        {p.steps.map(s => (
          <li key={s.id} className={`ml-start__step${s.done ? ' is-done' : ''}`}>
            <span className="ml-start__tick" aria-hidden="true">{s.done && <CheckIcon size={14} />}</span>
            <div className="ml-start__body">
              {s.done || s.id === open
                ? <p className="ml-start__name">
                  {TITLES[s.id]}
                  {s.done && <span className="ml-visually-hidden">, done</span>}
                </p>
                : <button type="button" className="ml-start__name ml-start__pick" aria-expanded={false}
                  onClick={() => setPicked(s.id)}>{TITLES[s.id]}</button>}
              {s.id === open && <p className="ml-start__help">{help(s.id)}</p>}
              {s.id === open && <div className="ml-start__actions">{actions(s.id)}</div>}
            </div>
          </li>
        ))}
      </ol>
      {p.onClose && (
        <button type="button" className="ml-text-btn ml-start__hide ml-start__hide--foot" onClick={p.onHide}>Hide these steps</button>
      )}
    </section>
  )
}
